
import { useMemo } from 'react';
import cn from 'classnames';

import { Button, ButtonProps, Icon } from '@dansk-metal/ui';
import { formatDate } from '@dansk-metal/utils/date';

import { Address } from '@web/components/address/address';
import { eventMessages, EventType } from '@web/components/event-card/types';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import { EventDto } from '@web/services/crm-api/rest';

import styles from './event-detail-card.module.scss';

export type EventDetailCardProps = {
	status: string;
	event: EventDto;
	isLoading?: boolean;
	button: Pick<ButtonProps, 'onClick' | 'title' | 'disabled' | 'isLoading'>;
};

export function EventDetailCard({ status, event, isLoading, button: { title, ...buttonProps } }: EventDetailCardProps) {
	const registrationStatues: string[] = [
		'open-for-all',
		'invited-user',
		'registered-user',
		'not-logged-in-open-for-all',
		'not-logged-in-invitation-required',
	];

	const { registrationDeadline, startTime, endTime, location, capacity } = event;
	const eventType = eventMessages[status];

	const Label = ({ className }) => useMemo(() => (
		<>
			{'message' in eventType ? (
				<div className={cn(className, styles[`label__${status}`])}>
					<div className={styles.info_container}>
						<p className={styles.message}>{eventType.message}</p>
						{eventType?.description && <p className={styles.desription}>{eventType?.description}</p>}
					</div>
				</div>
			) : null}
		</>
	), [className]);

	function EventButton() {
		if (registrationStatues.includes(status)) {
			return (
				<Button className={status === 'registered-user' ? styles.alt_button : ''} icon={{ icon: 'arrow-right', size: 24 }} {...buttonProps}>
					{title}
				</Button>
			);
		}

		return null;
	}

	function EventLocation() {
		if (location?.locationType === 'Online') {
			return (
				<div className={styles.section}>
					<div className={styles.section__title}>
						<Icon icon="location" size={24} />
						<strong>Sted</strong>
					</div>
					<p>Online</p>
				</div>
			);
		}

		return (
			<div className={styles.section}>
				<div className={styles.section__title}>
					<Icon icon="location" size={24} />
					<strong>Sted</strong>
				</div>
				<div>
					<p>{location?.locationName}</p>
					{location?.address && (
						<Address address={location.address} />
					)}
				</div>
			</div>
		);
	}

	function hasPercentageRemaining(percentage: number) {
		if (event.capacity?.total && event.capacity.remaining) {
			if (event.capacity.remaining === 0) return false;
			return event.capacity.remaining <= (event.capacity.total / percentage);
		}
	}

	return (
		<div className={styles.container}>
			{isLoading ? (
				<ThemedSkeleton className={styles.loader}/>
			) : (
				<>
					<Label className={cn(styles.label)} />
					<div className={`${styles.card_container} ${'message' in eventType ? styles[`card_container__${'alt'}`] : ''}`}>
						<EventButton/>
						<div className={styles.section}>
							<div className={styles.section__title}>
								<Icon icon="ticket" size={24} />
								<strong>Tilmelding</strong>
							</div>
							<ul>
								{registrationDeadline && (
									<li>
										Tilmeldingsfrist
										<strong className={cn({ [styles.highlight_red]: status === EventType.DeadlinePassed })}>
											{formatDate(registrationDeadline, 'Tir 22. feb, 09.00')}
										</strong>
									</li>
								)}
								<li>
									Ledige pladser
									<strong className={cn(
										{
											[styles.highlight_red]: (status === EventType.NoMoreSeats || hasPercentageRemaining(10)),
										},
									)}>
										{Number(capacity?.remaining) > 0 ? 'Ja' : 'Nej'}{hasPercentageRemaining(10) && ' (få tilbage)'}
									</strong>
								</li>
							</ul>
						</div>
						<div className={styles.section}>
							<div className={styles.section__title}>
								<Icon icon="calendar" size={24} />
								<strong>Dato og tid</strong>
							</div>
							<ul>
								{startTime && (
									<li>Start<strong>{formatDate(startTime, 'Tir 22. feb, 09.00')}</strong></li>
								)}
								{endTime && (
									<li>Slut<strong>{formatDate(endTime, 'Tir 22. feb, 09.00')}</strong></li>
								)}
							</ul>
						</div>
						<EventLocation />
					</div>
				</>
			)}

		</div>
	);
}
